import React from 'react';
import './AboutPage.css';

const AboutPage = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <h1>About <span className="blue-text">Sritha Solutions</span></h1>
          <p>We build digital solutions that solve real business problems</p>
        </div>
      </section>

      {/* Our Journey Section */}
      <section className="journey-section section">
        <div className="container">
          <h2 className="section-title">Our <span className="blue-text">Journey</span></h2>
          <div className="journey-content">
            <p className="journey-text">
              Sritha Solutions started with a simple belief: technology should solve real problems, not just look impressive. 
              What began as a small team of passionate developers has grown into a trusted technology partner for businesses across India.
            </p>
            <p className="journey-text">
              We started by helping local startups build their first digital products. Today, we work with businesses of all sizes, 
              from ambitious entrepreneurs to established companies, helping them leverage technology to grow and succeed.
            </p>
            <p className="journey-text">
              Our journey is driven by one mission: to build solutions that create lasting impact. Every project, every client, 
              every line of code is a step toward making technology more accessible, more effective, and more human.
            </p>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="how-we-work section">
        <div className="container">
          <h2 className="section-title">How We Work With Our <span className="blue-text">Clients</span></h2>
          <p className="section-subtitle">
            We follow a simple and transparent process to ensure every project is delivered successfully and meets your business goals.
          </p>
          <div className="work-steps">
            <div className="work-step">
              <div className="step-icon">🤝</div>
              <h3>Understand Your Idea</h3>
              <p>We start by understanding your idea and business needs</p>
            </div>
            <div className="work-step">
              <div className="step-icon">📊</div>
              <h3>Define Strategy</h3>
              <p>We define a clear strategy and roadmap for success</p>
            </div>
            <div className="work-step">
              <div className="step-icon">🎨</div>
              <h3>Design Experiences</h3>
              <p>We design intuitive and user-friendly experiences</p>
            </div>
            <div className="work-step">
              <div className="step-icon">⚙️</div>
              <h3>Build Solutions</h3>
              <p>We build scalable and high-performance solutions</p>
            </div>
            <div className="work-step">
              <div className="step-icon">🚀</div>
              <h3>Launch & Improve</h3>
              <p>We launch and continuously improve your product</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section className="expertise-section section">
        <div className="container">
          <h2 className="section-title">Our <span className="blue-text">Expertise</span></h2>
          <div className="expertise-grid">
            <div className="expertise-card">
              <span className="expertise-icon">🌐</span>
              <h3>Web & SaaS Development</h3>
              <p>Scalable web applications and SaaS platforms</p>
            </div>
            <div className="expertise-card">
              <span className="expertise-icon">📱</span>
              <h3>Mobile App Development</h3>
              <p>iOS and Android apps that users love</p>
            </div>
            <div className="expertise-card">
              <span className="expertise-icon">☁️</span>
              <h3>Cloud & DevOps Solutions</h3>
              <p>Scalable infrastructure and automated deployments</p>
            </div>
            <div className="expertise-card">
              <span className="expertise-icon">🤖</span>
              <h3>AI & Automation</h3>
              <p>Intelligent solutions for smarter businesses</p>
            </div>
            <div className="expertise-card">
              <span className="expertise-icon">🎨</span>
              <h3>UI/UX Design</h3>
              <p>Beautiful interfaces that convert users</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="approach-section section">
        <div className="container">
          <h2 className="section-title">Our Approach to Building <span className="blue-text">Products</span></h2>
          <div className="approach-content">
            <p>
              We don't just build software — we build solutions that align with your business goals. 
              Our approach focuses on understanding your vision, delivering high-quality products, 
              and ensuring long-term success.
            </p>
            <p>
              Every project starts with listening. We take the time to understand your industry, 
              your users, and your challenges. Then we apply our technical expertise to build 
              solutions that actually work — not just today, but for years to come.
            </p>
          </div>
        </div>
      </section>

      {/* What Makes Us Different Section */}
      <section className="different-section section">
        <div className="container">
          <h2 className="section-title">What Makes Us <span className="blue-text">Different</span></h2>
          <div className="different-grid">
            <div className="different-card">
              <div className="different-icon">💡</div>
              <p>We focus on understanding your business, not just requirements</p>
            </div>
            <div className="different-card">
              <div className="different-icon">🤝</div>
              <p>We treat every project as a long-term partnership</p>
            </div>
            <div className="different-card">
              <div className="different-icon">⭐</div>
              <p>We prioritize quality over shortcuts</p>
            </div>
            <div className="different-card">
              <div className="different-icon">📈</div>
              <p>We build solutions that are easy to scale and maintain</p>
            </div>
            <div className="different-card">
              <div className="different-icon">🔄</div>
              <p>We stay involved even after launch</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mindset Section */}
      <section className="mindset-section section">
        <div className="container">
          <h2 className="section-title">Our <span className="blue-text">Mindset</span></h2>
          <div className="mindset-content">
            <p className="mindset-quote">
              "Technology should solve real problems — not just look impressive."
            </p>
            <div className="mindset-values">
              <div className="mindset-value">
                <span className="value-icon">🎯</span>
                <h4>Problem-Solving Mindset</h4>
                <p>We focus on solving real business challenges, not just writing code</p>
              </div>
              <div className="mindset-value">
                <span className="value-icon">🌱</span>
                <h4>Long-Term Impact</h4>
                <p>We build solutions that create lasting value for your business</p>
              </div>
              <div className="mindset-value">
                <span className="value-icon">📈</span>
                <h4>Continuous Improvement</h4>
                <p>We never stop learning and evolving to serve you better</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Can Expect Section */}
      <section className="expect-section section">
        <div className="container">
          <h2 className="section-title">What You Can Expect Working <span className="blue-text">With Us</span></h2>
          <div className="expect-grid">
            <div className="expect-card">
              <div className="expect-icon">💬</div>
              <p>Clear and honest communication</p>
            </div>
            <div className="expect-card">
              <div className="expect-icon">⏰</div>
              <p>On-time delivery without surprises</p>
            </div>
            <div className="expect-card">
              <div className="expect-icon">🔍</div>
              <p>Transparent process from start to finish</p>
            </div>
            <div className="expect-card">
              <div className="expect-icon">❤️</div>
              <p>A team that actually cares about your success</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision Section */}
      <section className="vision-section section">
        <div className="container">
          <h2 className="section-title">Our Vision for Our <span className="black-text">Clients</span></h2>
          <div className="vision-content">
            <p>
              We aim to help businesses not just build products, but create digital solutions that grow, 
              scale, and succeed in the long run. Your success is our success — and we're committed to 
              being your trusted technology partner every step of the way.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;